/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode
} from "react";

// ============================================================================
// BREADCRUMB OVERRIDE CONTEXT
// ============================================================================
// Allows any page to dynamically replace a URL path segment (e.g. a GUID)
// with a human-readable label in the shell breadcrumb.

interface BreadcrumbOverrideContextValue {
  overrides: Map<string, string>;
  setLabelOverride: (segment: string, label: string) => void;
  removeLabelOverride: (segment: string) => void;
}

const BreadcrumbOverrideContext = createContext<
  BreadcrumbOverrideContextValue | undefined
>(undefined);

export function BreadcrumbOverrideProvider({
  children
}: {
  children: ReactNode;
}) {
  const [overrides, setOverrides] = useState<Map<string, string>>(
    () => new Map()
  );

  const setLabelOverride = useCallback((segment: string, label: string) => {
    setOverrides((prev) => {
      if (prev.get(segment) === label) return prev;
      const next = new Map(prev);
      next.set(segment, label);
      return next;
    });
  }, []);

  const removeLabelOverride = useCallback((segment: string) => {
    setOverrides((prev) => {
      if (!prev.has(segment)) return prev;
      const next = new Map(prev);
      next.delete(segment);
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({ overrides, setLabelOverride, removeLabelOverride }),
    [overrides, setLabelOverride, removeLabelOverride]
  );

  return (
    <BreadcrumbOverrideContext.Provider value={value}>
      {children}
    </BreadcrumbOverrideContext.Provider>
  );
}

export function useBreadcrumbOverrides() {
  const ctx = useContext(BreadcrumbOverrideContext);
  if (!ctx) {
    throw new Error(
      "useBreadcrumbOverrides must be used within a BreadcrumbOverrideProvider"
    );
  }
  return ctx;
}

/**
 * Hook for pages to set a breadcrumb label override.
 * Automatically cleans up when the component unmounts.
 */
export function useBreadcrumbLabel(
  segment: string | undefined,
  label: string | undefined
) {
  const { setLabelOverride, removeLabelOverride } = useBreadcrumbOverrides();

  useEffect(() => {
    if (segment && label) {
      setLabelOverride(segment, label);
    }
    return () => {
      if (segment) {
        removeLabelOverride(segment);
      }
    };
  }, [segment, label, setLabelOverride, removeLabelOverride]);
}
